import { compact as _compact, trim as _trim, sortBy as _sortBy } from 'lodash-es';
import { useState, Fragment, type ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import EventIcon from '@mui/icons-material/Event';
import removeMarkdown from 'remove-markdown';
import moment from 'moment';
import config from '../../config';
import { eventPath } from '../../utils/slugify';
import { eventAgeRestrictions, eventDistances, eventPaces, eventRegroups, eventTerrains, eventTypes, eventWeatherCancellations, prettyVisibility } from '../../utils/events';
import { getEventCategoryIdsForType, getEventCategoryName } from '../../utils/event-categories';
import type { Event } from '../../types/Event';

const useStyles = makeStyles()((theme) => ({
  trigger: {
    display: 'flex',
    alignItems: 'center',
  },
  eventIcon: {
    marginRight: theme.spacing(0.5),
  },
}));

const EVENT_TYPE_EMOJI: Record<string, string> = {
  RIDE: '🚴',
  EVENT: '📌',
  ONLINE_EVENT: '💻',
};

// Mirrors buildEvent() in server/src/data-routes.js
const buildEventDescription = (event: Event) => {
  const { eventType, user, group, hostType } = event;
  const approved = event.permissions?.approved;
  const isCancelled = event.status === 'CANCELLED';
  const eventUrl = `${config().universal.baseUri}${eventPath(event.id, event.name)}`;

  // --- Build host string ---
  let host: string | undefined;
  if (hostType === 'Group' && group) {
    host = group.name;
  } else if (hostType === 'User' && user) {
    const displayName = (user.givenName || user.familyName) ?
      _compact([user.givenName, user.familyName]).join(' ') :
      user.username;
    host = `${displayName}${group ? ` in ${group.name}` : ''}`;
  }

  // --- Build headline: emoji + type + ride metrics ---
  const emoji = EVENT_TYPE_EMOJI[eventType] || '📌';
  const typeName = eventTypes[eventType].name;
  const headlineParts: string[] = [typeName];
  if (event.pace) {
    headlineParts.push(`${eventPaces[event.pace].name} (${eventPaces[event.pace].description})`);
  }
  if (event.distance) {
    headlineParts.push(eventDistances[event.distance].name);
  }
  const headline = `${emoji} ${headlineParts.join(' · ')}`;

  // --- Build ride details line 2 (terrain, regroup, weather, loop) ---
  const rideDetails: string[] = [];
  if (event.terrain) {
    rideDetails.push(eventTerrains[event.terrain].name);
  }
  if (event.regroup) {
    rideDetails.push(eventRegroups[event.regroup].name);
  }
  if (event.weatherCancellation) {
    rideDetails.push(eventWeatherCancellations[event.weatherCancellation].name);
  }
  if (event.loop) {
    rideDetails.push('Loop');
  }

  // --- Build custom categories list ---
  const categoryIdsForType = getEventCategoryIdsForType(eventType);
  const sortedCategoryIds = _sortBy(event.categoryIds, (categoryId) => categoryIdsForType.indexOf(categoryId));
  const categoryNames = _compact(sortedCategoryIds.map((categoryId) => getEventCategoryName(categoryId)));

  // --- Build contact string ---
  const contactParts: string[] = [];
  if (event.contactName) {
    contactParts.push(event.contactName);
  }
  if (event.contactEmail) {
    contactParts.push(event.contactEmail);
  }
  if (event.contactPhoneNumberDisplay) {
    contactParts.push(event.contactPhoneNumberDisplay);
  }

  // --- Assemble description (sections joined with \n\n, lines within sections with \n) ---
  const sections: string[] = [];

  if (isCancelled) {
    // Cancelled events: lead with cancellation, then abbreviated details
    sections.push('CANCELLED');
    if (event.cancellationMessage) {
      sections.push(event.cancellationMessage);
    }
    sections.push('───');
    const cancelledLines = [headline];
    if (host) {
      cancelledLines.push(`👤 Hosted by ${host}`);
    }
    if (event.series?.name) {
      cancelledLines.push(`🔁 ${event.series.name} series`);
    }
    if (approved && event.startLocation) {
      cancelledLines.push(`📍 ${event.startLocation}`);
    }
    sections.push(cancelledLines.join('\n'));
    sections.push(`More details → ${eventUrl}`);
  } else {
    // "At a Glance" block — location, timing, headline, route, host
    const atAGlance: string[] = [];

    if (approved && event.startLocation) {
      let locationLine = `📍 ${event.startLocation}`;
      if (event.startLocationAddress) {
        locationLine += ` — ${event.startLocationAddress}`;
      }
      atAGlance.push(locationLine);
      if (event.startLocationDetails) {
        atAGlance.push(event.startLocationDetails);
      }
    }

    if (approved && event.startAtDetails) {
      atAGlance.push(`⏰ ${event.startAtDetails}`);
    }

    atAGlance.push(headline);
    if (rideDetails.length > 0) {
      atAGlance.push(rideDetails.join(' · '));
    }

    // Restrictions (in at-a-glance because they affect who should show up)
    if (event.ageRestriction !== 'GENERAL') {
      atAGlance.push(`${eventAgeRestrictions[event.ageRestriction].name} (${eventAgeRestrictions[event.ageRestriction].description})`);
    }
    if (event.audienceRestriction) {
      atAGlance.push(`⚠️ ${event.audienceRestriction}`);
    }

    if (approved && event.routeUrl) {
      atAGlance.push(`🔗 Route: ${event.routeUrl}`);
    }

    if (host) {
      atAGlance.push(`👤 Hosted by ${host}`);
    }

    if (event.series?.name) {
      atAGlance.push(`🔁 ${event.series.name} series`);
    }

    sections.push(atAGlance.join('\n'));

    // Registration callout
    if (event.registrationType === 'APPROVAL_REQUIRED' && !approved) {
      sections.push(`🔐 RSVP required — request access at ${eventUrl}`);
    } else if (event.registrationType === 'EXTERNAL_REGISTRATION') {
      sections.push(`🔗 Registration required — see event page for details`);
    }

    sections.push('───');

    // Event description text
    const descriptionTexts: string[] = [];
    if (event.description) {
      descriptionTexts.push(removeMarkdown(_trim(event.description)));
    }
    if (approved && event.approvalDescription) {
      descriptionTexts.push(removeMarkdown(_trim(event.approvalDescription)));
    }
    if (descriptionTexts.length > 0) {
      sections.push(descriptionTexts.join('\n\n'));
    }

    sections.push('───');

    // End location (start location moved to at-a-glance)
    if (approved && eventType === 'RIDE' && event.endLocation) {
      const endLines = [`📍 End: ${event.endLocation}`];
      if (event.endLocationAddress) {
        endLines.push(event.endLocationAddress);
      }
      if (event.endLocationDetails) {
        endLines.push(event.endLocationDetails);
      }
      sections.push(endLines.join('\n'));
    }

    // Links + contact section (route moved to at-a-glance)
    const linkContactLines: string[] = [];
    if (approved && event.infoUrl) {
      linkContactLines.push(`ℹ️ More info: ${event.infoUrl}`);
    }
    if (contactParts.length > 0) {
      linkContactLines.push(`📧 Contact: ${contactParts.join(' · ')}`);
    }
    if (linkContactLines.length > 0) {
      sections.push(linkContactLines.join('\n'));
    }

    // Tags (visibility + custom categories)
    const tags: string[] = [];
    if (event.visibility !== 'PUBLIC') {
      tags.push(prettyVisibility(event, { group }));
    }
    tags.push(...categoryNames);
    if (tags.length > 0) {
      sections.push(tags.join(', '));
    }

    sections.push(`More details & RSVP → ${eventUrl}`);
  }

  return sections.join('\n\n');
};

type Props = {
  event: Event;
  buttonComponent?: React.ElementType;
  children?: ReactNode;
  tooltip?: string;
  className?: string;
  [key: string]: unknown;
};

const AddToCalendar = ({ event, buttonComponent: ButtonComponent = Button, children, tooltip, className, ...rest }: Props) => {
  const { classes, cx } = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  if (!event.startAt) {
    return null;
  }

  function handleOpen(event) {
    event.preventDefault()
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleSelect(item) {
    if (item === 'google') {
      const startAt = moment.utc(event.startAt);
      const endAt = event.endAt ? moment.utc(event.endAt) : startAt;
      const googleFormat = 'YYYYMMDDTHHmmss';

      const location = event.permissions.approved ?
        _compact([
          event.startLocation,
          event.startLocationAddress,
        ]).join(', ') :
        'Location hidden';

      window.location.href = `https://www.google.com/calendar/render?${[
        'action=TEMPLATE',
        `text=${encodeURI(event.name)}`,
        `dates=${startAt.format(googleFormat)}Z/${endAt.format(googleFormat)}Z`,
        `ctz=${event.timezone}`,
        `details=${encodeURI(buildEventDescription(event))}`,
        `location=${location}`
      ].join('&')}`;
      return;
    }
    window.location.href = `${config().universal.baseUri}/events/${event.id}/event.ics?token=${event.exportToken}`;
  }

  const button = (
    <ButtonComponent
      aria-controls="add-to-calendar"
      aria-haspopup="true"
      onClick={handleOpen}
      className={cx(children ? undefined : classes.trigger, className)}
      {...rest}
    >
      {children ?? (
        <>
          <EventIcon className={classes.eventIcon} />
          <span>Add to Calendar</span>
        </>
      )}
    </ButtonComponent>
  );

  return (
    <Fragment>
      {tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button}
      <Menu
        id="add-to-calendar"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={() => handleSelect('google')}>Add to Google Calendar</MenuItem>
        <MenuItem onClick={() => handleSelect('outlook')}>Add to Outlook Calendar</MenuItem>
        <MenuItem onClick={() => handleSelect('apple')}>Add to Apple Calendar</MenuItem>
        <MenuItem onClick={() => handleSelect('ics')}>Download .ics</MenuItem>
      </Menu>
    </Fragment>
  );
};

export default AddToCalendar;
